import React, { createContext, useContext, useState } from "react";
import Cookies from "js-cookie";

import type { AuthRole } from "../types/auth";

interface AuthContextProps {
  isAuthenticated: boolean;
  role: AuthRole | null;
  login: (role: AuthRole) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const readRole = (): AuthRole | null => {
  const value = Cookies.get("auth_role");
  return value === "teacher" || value === "student" ? value : null;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<AuthRole | null>(() => readRole());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const hasTokens = Boolean(Cookies.get("access_token") && Cookies.get("refresh_token"));
    const hasRole = Boolean(readRole());
    if ((!hasTokens || !hasRole) && localStorage.getItem("isAuthenticated") === "true") {
      localStorage.removeItem("isAuthenticated");
    }
    return hasTokens && hasRole;
  });

  const login = (nextRole: AuthRole) => {
    Cookies.set("auth_role", nextRole);
    setRole(nextRole);
    setIsAuthenticated(true);
    localStorage.setItem("isAuthenticated", "true");
  };

  const logout = () => {
    setIsAuthenticated(false);
    setRole(null);
    localStorage.removeItem("isAuthenticated");
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
    Cookies.remove("auth_role");
    Cookies.remove("user_id");
    Cookies.remove("student_id");
    Cookies.remove("email");
    Cookies.remove("first_name");
    Cookies.remove("last_name");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, role, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
